/**
 * Product Hub — commerce: stores, and what a store says about a product.
 *
 * The rule this whole file exists to serve is the spec's §51-53: **a store never holds a copy of
 * a central value.** It holds an override or nothing, and the answer to "what does this store
 * actually show?" is resolved, not stored. `resolveStoreProductFields` is that resolution, and it
 * returns `Effective<T>` so the caller receives the answer *and* both inputs *and* the reason —
 * a UI that gets only the answer cannot draw the "יורש מהמוצר המרכזי" hint, and one that gets
 * only the inputs would have to re-implement the rule.
 *
 * Codes are English and labels are Hebrew (ADR-017). No React, no MUI, no browser globals — the
 * backend imports this file directly through `@malash-office/types/product-hub/commerce`.
 */
import { Effective, PhOption, phOptions, resolveEffective } from './productHub.core';

/* ------------------------------------------------------------------ */
/* Stores                                                              */
/* ------------------------------------------------------------------ */

/**
 * The e-commerce platform a store runs on.
 *
 * `other` is not a placeholder for "we haven't decided" — it is the value for a store the system
 * manages but does not sync to, which is a real case: a physical shop tracked here for inventory
 * and pricing with no site behind it at all.
 */
export const STORE_PLATFORMS = ['woocommerce', 'shopify', 'custom', 'other'] as const;
export type StorePlatform = (typeof STORE_PLATFORMS)[number];

export const STORE_PLATFORM_LABELS: Record<StorePlatform, string> = {
  woocommerce: 'WooCommerce',
  shopify: 'Shopify',
  custom: 'פיתוח עצמאי',
  other: 'אחר'
};

export const STORE_PLATFORM_OPTIONS: PhOption<StorePlatform>[] = phOptions(STORE_PLATFORMS, STORE_PLATFORM_LABELS);

/**
 * Whose store this is — the commercial relationship, not the technology.
 *
 * Deliberately **not** folded into `platform`, which answers a different question: a WooCommerce
 * site can be ours or a reseller's, and a store with no site at all (`platform: 'other'`) is
 * usually the most "ours" of the lot. The two would fight the moment either list grew.
 *
 * The pricelist opens on `own_store` only. That is the whole reason the field exists: a matrix
 * that puts every reseller's column next to ours by default is unreadable on the screen most
 * people use it on, and "our prices" is the question being asked nine times out of ten.
 */
export const STORE_TYPES = ['own_store', 'external'] as const;
export type StoreType = (typeof STORE_TYPES)[number];

export const STORE_TYPE_LABELS: Record<StoreType, string> = {
  own_store: 'חנות שלנו',
  external: 'חנות חיצונית'
};

export const STORE_TYPE_OPTIONS: PhOption<StoreType>[] = phOptions(STORE_TYPES, STORE_TYPE_LABELS);

/**
 * A store's domain, and the one shape it is allowed to be stored in.
 *
 * The column is an **identity**, not a link: an incoming webhook is matched to a store by it, so
 * `https://shop.example.com/products/` and `shop.example.com` must resolve to the same store or
 * they become two. The schema therefore accepts only the bare host — which is exactly what made
 * creating a store fail for anyone who did the obvious thing and pasted a URL out of the address
 * bar, and got back nothing but "Validation failed".
 *
 * So the rule is normalise-then-validate, in one place both sides use: the frontend runs it so the
 * field shows what will be saved, and the backend runs it inside the Joi schema so a client that
 * skips it — or a different client entirely — cannot write a second spelling of the same host.
 *
 * `www.` is deliberately **not** stripped. It is a different host, and folding it here would merge
 * two stores that a customer, and a webhook, can tell apart.
 */
export const STORE_DOMAIN_PATTERN = /^[a-z0-9.-]+$/;

export const normalizeStoreDomain = (value: string | null | undefined): string => {
  if (!value) return '';

  return (
    value
      .trim()
      .toLowerCase()
      // scheme, then credentials — in that order, or `user:pass@` eats the `//`
      .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
      .replace(/^[^@/]*@/, '')
      // path, query and fragment: the first of them ends the host
      .split(/[/?#]/)[0]
      // an explicit port is not part of the identity
      .replace(/:\d+$/, '')
      // a fully-qualified name's trailing dot is legal DNS and not how anybody stores it
      .replace(/\.$/, '')
  );
};

/**
 * ISO 3166-1 alpha-2, e.g. `IL`. Two *letters* — the column used to take any two characters, so a
 * `12` typed into a field labelled "קוד דו-אותי" was stored as a country.
 */
export const STORE_COUNTRY_PATTERN = /^[A-Z]{2}$/;

export const STORE_LANGUAGES = ['he', 'en', 'fr', 'es', 'ru'] as const;
export type StoreLanguage = (typeof STORE_LANGUAGES)[number];

export const STORE_LANGUAGE_LABELS: Record<StoreLanguage, string> = {
  he: 'עברית',
  en: 'אנגלית',
  fr: 'צרפתית',
  es: 'ספרדית',
  ru: 'רוסית'
};

export const STORE_LANGUAGE_OPTIONS: PhOption<StoreLanguage>[] = phOptions(STORE_LANGUAGES, STORE_LANGUAGE_LABELS);

/* ------------------------------------------------------------------ */
/* Publishing                                                          */
/* ------------------------------------------------------------------ */

/**
 * Where a product stands in one store.
 *
 * Separate from `active` and not merged into it, because they answer different questions.
 * `active` is ours — "should this store carry the product at all". `publishStatus` describes the
 * store's own state, which a sync will eventually write back: a product can be `active` here and
 * still `draft` over there because the site has not accepted it yet.
 */
export const PUBLISH_STATUSES = ['draft', 'published', 'unpublished', 'scheduled'] as const;
export type PublishStatus = (typeof PUBLISH_STATUSES)[number];

export const PUBLISH_STATUS_LABELS: Record<PublishStatus, string> = {
  draft: 'טיוטה',
  published: 'מפורסם',
  unpublished: 'הוסר מפרסום',
  scheduled: 'מתוזמן'
};

export const PUBLISH_STATUS_OPTIONS: PhOption<PublishStatus>[] = phOptions(PUBLISH_STATUSES, PUBLISH_STATUS_LABELS);

/* ------------------------------------------------------------------ */
/* Overrides                                                           */
/* ------------------------------------------------------------------ */

/** Where a store-facing value came from. */
export const STORE_FIELD_SOURCES = ['store_override', 'product', 'missing'] as const;
export type StoreFieldSource = (typeof STORE_FIELD_SOURCES)[number];

export const STORE_FIELD_SOURCE_LABELS: Record<StoreFieldSource, string> = {
  store_override: 'ערך מותאם לחנות',
  product: 'יורש מהמוצר המרכזי',
  missing: 'אין ערך'
};

export type EffectiveStoreField<T> = Effective<T, StoreFieldSource>;

/** One inheritable field, resolved. */
export const resolveStoreField = <T>(base: T | null | undefined, override: T | null | undefined) =>
  resolveEffective<T, StoreFieldSource>(base, override, {
    whenOverride: 'store_override',
    whenBase: 'product',
    whenMissing: 'missing',
    labels: STORE_FIELD_SOURCE_LABELS
  });

/** The central values a store may override. */
export interface StoreProductBase {
  name: string | null;
  shortDescription: string | null;
  description: string | null;
  categoryId: number | null;
}

/** What the `StoreProducts` row holds. Every field is null unless the store overrode it. */
export interface StoreProductOverrides {
  nameOverride: string | null;
  shortDescriptionOverride: string | null;
  descriptionOverride: string | null;
  categoryOverrideId: number | null;
}

export interface EffectiveStoreProduct {
  name: EffectiveStoreField<string>;
  shortDescription: EffectiveStoreField<string>;
  description: EffectiveStoreField<string>;
  categoryId: EffectiveStoreField<number>;
}

/**
 * What this store actually shows for this product.
 *
 * Called by the backend on the way out, never by the frontend: §53 makes "the client does not
 * decide inheritance" a rule rather than a preference, and the only way to enforce it is for the
 * client to never receive the raw pair without the resolved answer beside it.
 *
 * An empty-string override is treated as a real override, not as "unset" — a store that wants a
 * blank short description is expressing an intent, and `clearOverride` writes `null` for the
 * other case.
 */
export const resolveStoreProductFields = (
  base: StoreProductBase,
  overrides: StoreProductOverrides | null
): EffectiveStoreProduct => ({
  name: resolveStoreField(base.name, overrides?.nameOverride),
  shortDescription: resolveStoreField(base.shortDescription, overrides?.shortDescriptionOverride),
  description: resolveStoreField(base.description, overrides?.descriptionOverride),
  categoryId: resolveStoreField(base.categoryId, overrides?.categoryOverrideId)
});

/* ------------------------------------------------------------------ */
/* Sales rules                                                         */
/* ------------------------------------------------------------------ */

/** What happens to the rest of an order when a preordered item is not ready yet (spec §14). */
export const PREORDER_SHIPPING_POLICIES = ['wait_and_ship_together', 'ship_available_items_separately'] as const;
export type PreorderShippingPolicy = (typeof PREORDER_SHIPPING_POLICIES)[number];

export const PREORDER_SHIPPING_POLICY_LABELS: Record<PreorderShippingPolicy, string> = {
  wait_and_ship_together: 'להמתין ולשלוח הכל יחד',
  ship_available_items_separately: 'לשלוח בנפרד את הזמין'
};

export const PREORDER_SHIPPING_POLICY_OPTIONS = phOptions(PREORDER_SHIPPING_POLICIES, PREORDER_SHIPPING_POLICY_LABELS);

export interface ProductSalesRules {
  minQuantity: number;
  maxQuantityPerOrder: number | null;
  sellInMultiples: number | null;
  requiresAdditionalProducts: boolean;
  preorderEnabled: boolean;
  preorderEstimatedDate: string | null;
  preorderCustomerMessage: string | null;
  preorderShippingPolicy: PreorderShippingPolicy | null;
  /** Variants of which at least one must be in the same order (spec §13, MVP rule). */
  requiredVariantIds: number[];
}

/**
 * Is this an order quantity the rules allow?
 *
 * Pure, and shared, because the answer has to be identical in three places that will otherwise
 * drift: the wizard's live warning, the API's rejection, and eventually the store sync's
 * validation. The multiples rule is the one that is easy to get subtly wrong — it counts from
 * the minimum, not from zero, so a product sold in pairs starting at 3 allows 3, 5, 7 and not 4.
 */
export const validateOrderQuantity = (
  quantity: number,
  rules: Pick<ProductSalesRules, 'minQuantity' | 'maxQuantityPerOrder' | 'sellInMultiples'>
): { ok: true } | { ok: false; message: string } => {
  if (!Number.isInteger(quantity) || quantity <= 0) return { ok: false, message: 'כמות חייבת להיות מספר שלם חיובי' };

  if (quantity < rules.minQuantity) return { ok: false, message: `כמות מינימלית להזמנה: ${rules.minQuantity}` };

  if (rules.maxQuantityPerOrder !== null && quantity > rules.maxQuantityPerOrder) {
    return { ok: false, message: `כמות מקסימלית להזמנה: ${rules.maxQuantityPerOrder}` };
  }

  if (rules.sellInMultiples && rules.sellInMultiples > 1) {
    const step = rules.sellInMultiples;
    if ((quantity - rules.minQuantity) % step !== 0) {
      return { ok: false, message: `המוצר נמכר בכפולות של ${step}` };
    }
  }

  return { ok: true };
};
